/**
 * Require auth user from Supabase session (no throw).
 * Use when you need the current auth user for DB writes or scoped reads.
 * Returns { user: null } when there is no session or no user — do not throw.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { User } from '@supabase/supabase-js';

export interface RequireAuthUserResult {
  user: User | null;
}

/**
 * Gets the current Supabase auth user from session.
 * Calls getSession() first. Does not throw; returns { user: null } when missing.
 */
export async function requireAuthUser(supabase: SupabaseClient): Promise<RequireAuthUserResult> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      // DEBUG: remove after diagnosis
      console.log('[AUTH] no session/user', { hasSession: !!session, expires_at: session?.expires_at, userId: session?.user?.id });
      try {
        const supabaseUrlFromEnvOrClientIfAccessible =
          (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SUPABASE_URL) ||
          (supabase as any).supabaseUrl ||
          undefined;
        if (supabaseUrlFromEnvOrClientIfAccessible != null) {
          console.log('[AUTH] supabase url', supabaseUrlFromEnvOrClientIfAccessible);
        }
      } catch {
        /* skip URL log if not accessible */
      }
      return { user: null };
    }
    return { user: session.user };
  } catch {
    return { user: null };
  }
}
